import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { frequencyKey, localInputToUtc, SETUP_FREQUENCIES, setupLevelsIssue, utcToLocalInput } from '@vtf/shared';
import { api, errorText } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtTime } from '../lib/format';
import type { SetupDetail, SetupFormOptions } from '../lib/types-setups';
import { Button, Card, ErrorText, Field, Input, PageHeader, Pill, Segmented, Select, Textarea } from '../components/ui';
import { SIGNAL_TONE, type SignalView } from '../components/SignalFeedItem';

/** GET /signals/:id/setup-draft — "Make a setup from this" (INT-SYN-02). */
interface SignalDraft {
  signal: SignalView;
  existingSetupId: string | null;
  draft: { signalId: string; instrumentId: string; direction: 'LONG' | 'SHORT' | null; timeframe: string; zoneLow: string | null; zoneHigh: string | null; invalidationPrice: string | null; targetIdea: string | null; triggerCondition: string };
}

const TIMEFRAMES = ['M5', 'M15', 'M30', 'H1', 'H4', 'D1', 'W1'];
const CONFIDENCE = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

const ruleText = (rule: string | number | undefined, code: string) =>
  rule === 'end_of_day' ? `end of the ${code} trading day` : typeof rule === 'number' ? `${Math.round(rule / 60)} h after you mark it` : `end of the ${code} trading week`;

/** Mark a setup (INT-STP-01/02) or edit one (symbol and direction stay fixed). */
export function SetupForm() {
  const { id } = useParams();
  const editing = !!id;
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const tz = me.data?.timezone ?? 'UTC';
  const { data: existing } = useQuery({ queryKey: ['setup', id], queryFn: () => api<SetupDetail>(`/setups/${id}`), enabled: editing });
  const [params] = useSearchParams();
  const signalId = editing ? null : params.get('signal');
  const fromSignal = useQuery({ queryKey: ['signal-draft', signalId], queryFn: () => api<SignalDraft>(`/signals/${signalId}/setup-draft`), enabled: !!signalId, retry: false });

  const sigInst = fromSignal.data?.signal.instrument;
  // a Master Trader may mark a setup on a signal's symbol without holding it
  const symbols = useMemo(() => {
    const mine = [...(me.data?.symbols ?? [])];
    if (sigInst && !mine.some((x) => x.id === sigInst.id)) mine.push({ id: sigInst.id, symbol: sigInst.symbol, marketCode: sigInst.market } as (typeof mine)[number]);
    return mine.sort((a, b) => a.marketCode.localeCompare(b.marketCode) || a.symbol.localeCompare(b.symbol));
  }, [me.data, sigInst]);
  const [instrumentId, setInstrumentId] = useState('');
  const [direction, setDirection] = useState<'LONG' | 'SHORT'>('LONG');
  const [timeframe, setTimeframe] = useState('H1');
  const [strategyId, setStrategyId] = useState('');
  const [zoneLow, setZoneLow] = useState('');
  const [zoneHigh, setZoneHigh] = useState('');
  const [trigger, setTrigger] = useState('');
  const [invalidation, setInvalidation] = useState('');
  const [target, setTarget] = useState('');
  const [confidence, setConfidence] = useState<string>('MEDIUM');
  const [freq, setFreq] = useState('1h');
  const [customMinutes, setCustomMinutes] = useState('45');
  const [ownExpiry, setOwnExpiry] = useState(false);
  const [expiryLocal, setExpiryLocal] = useState('');
  const [chartUrl, setChartUrl] = useState('');

  const loaded = useRef(false);
  useEffect(() => {
    if (!existing || loaded.current) return;
    loaded.current = true;
    setInstrumentId(existing.instrumentId);
    setDirection(existing.direction);
    setTimeframe(existing.timeframe);
    setStrategyId(existing.strategy?.id ?? '');
    setZoneLow(existing.zoneLow ?? '');
    setZoneHigh(existing.zoneHigh ?? '');
    setTrigger(existing.triggerCondition);
    setInvalidation(existing.invalidationPrice ?? '');
    setTarget(existing.targetIdea ?? '');
    setConfidence(existing.confidence ?? 'MEDIUM');
    const key = frequencyKey(existing.cadence, existing.cadenceMinutes);
    setFreq(key);
    if (key === 'custom') setCustomMinutes(String(existing.cadenceMinutes ?? 45));
    setExpiryLocal(utcToLocalInput(existing.expiresAt, tz));
    setChartUrl(existing.chartUrl ?? '');
  }, [existing, tz]);

  // pre-fill once from the signal; the trader reviews and changes anything before saving
  useEffect(() => {
    const d = fromSignal.data?.draft;
    if (!d || loaded.current) return;
    loaded.current = true;
    setInstrumentId(d.instrumentId);
    if (d.direction) setDirection(d.direction);
    setTimeframe(d.timeframe);
    setZoneLow(d.zoneLow ?? '');
    setZoneHigh(d.zoneHigh ?? '');
    setInvalidation(d.invalidationPrice ?? '');
    setTarget(d.targetIdea ?? '');
    setTrigger(d.triggerCondition);
  }, [fromSignal.data]);

  const inst = editing ? existing?.instrumentId : instrumentId || symbols[0]?.id;
  const { data: opts } = useQuery({ queryKey: ['setup-options', inst], queryFn: () => api<SetupFormOptions>(`/setups/form-options?instrumentId=${inst}`), enabled: !!inst });
  const market = opts?.market;
  const preset = SETUP_FREQUENCIES.find((f) => f.key === freq)!;
  const minutes = freq === 'custom' ? Number(customMinutes) : preset.minutes;
  const levelsIssue = zoneLow && invalidation && Number(zoneLow) > 0 && Number(invalidation) > 0 ? setupLevelsIssue({ direction: editing && existing ? existing.direction : direction, zoneLow, zoneHigh: zoneHigh || null, invalidationPrice: invalidation }) : null;
  const ready = !!inst && Number(zoneLow) > 0 && Number(invalidation) > 0 && trigger.trim().length >= 3 && !levelsIssue && (preset.cadence !== 'INTERVAL' || minutes! >= 5) && (!ownExpiry || !!expiryLocal);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        timeframe,
        strategyId: strategyId || null,
        zoneLow,
        zoneHigh: zoneHigh || null,
        triggerCondition: trigger,
        invalidationPrice: invalidation,
        targetIdea: target || null,
        confidence,
        cadence: preset.cadence,
        cadenceMinutes: preset.cadence === 'INTERVAL' ? minutes : null,
        chartUrl,
        ...(ownExpiry && expiryLocal ? { expiresAt: localInputToUtc(expiryLocal, tz)?.toISOString() } : {}),
      };
      return editing ? api<SetupDetail>(`/setups/${id}`, { method: 'PATCH', body }) : api<SetupDetail>('/setups', { body: { ...body, instrumentId: inst, direction, ...(signal && signal.instrument.id === inst ? { signalId: signal.id } : {}) } });
    },
    onSuccess: (s) => {
      void qc.invalidateQueries({ queryKey: ['setups'] });
      void qc.invalidateQueries({ queryKey: ['setup', s.id] });
      void qc.invalidateQueries({ queryKey: ['today'] });
      nav(`/setups/${s.id}`);
    },
  });

  const signal = fromSignal.data?.signal ?? null;
  if (!me.data) return null;
  if (!editing && !symbols.length) return <Card className="p-5 text-sub">You have no assigned symbols yet. Ask an admin or Master Trader to assign some.</Card>;
  if (editing && !existing) return null;
  const sym = editing ? existing!.symbol : symbols.find((s) => s.id === inst)?.symbol;

  const cadenceHint =
    preset.cadence === 'SESSION_OPEN'
      ? market?.nextOpen
        ? `Next ${market.code} open: ${fmtTime(market.nextOpen, tz, true)}.${market.is24h ? ` ${market.code} trades round the clock, so this is its weekly open.` : ''}`
        : ''
      : preset.cadence === 'SESSION_CLOSE'
        ? market?.nextClose
          ? `Next ${market.code} close: ${fmtTime(market.nextClose, tz, true)}.${market.is24h ? ` ${market.code} trades round the clock, so this is its weekly close.` : ''}`
          : ''
        : `Updates that fall while ${market?.code ?? 'the market'} is closed move to the next open. During your quiet hours they wait until the end, unless someone covers you.`;

  return (
    <>
      <PageHeader
        back={<Link to={editing ? `/setups/${id}` : '/setups'} className="text-[13px]">← {editing ? 'Setup' : 'Setups'}</Link>}
        title={editing ? `Edit setup · ${sym} ${existing!.direction === 'LONG' ? 'long' : 'short'}` : `Mark a setup${sym ? ` · ${sym} ${direction === 'LONG' ? 'long' : 'short'}` : ''}`}
        sub="Flag a level you're watching so the floor knows it might trigger."
        right={
          <Button variant="primary" onClick={() => save.mutate()} disabled={!ready || save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Mark setup'}
          </Button>
        }
      />
      <ErrorText error={save.error ? errorText(save.error) : fromSignal.error ? errorText(fromSignal.error) : null} />
      {signal && (
        <Card tone="accent" className="flex flex-col gap-1.5 px-4 py-3">
          <span className="flex flex-wrap items-center gap-2">
            <Pill tone={SIGNAL_TONE[signal.event]}>{signal.eventLabel}</Pill>
            <span>
              Pre-filled from the <strong>{signal.source.name}</strong> signal: {signal.instrument.symbol} {signal.strategy} on {signal.timeframe} · {fmtTime(signal.receivedAt, tz, true)}
            </span>
          </span>
          <span className="text-xs text-muted">
            Zone from the signal's {signal.entry ? 'entry' : 'price'}, invalidation from its stop, targets from its targets. Check every level before you mark it — the setup is yours.
            {signal.instrument.id !== inst && ' You picked another symbol, so this setup will not be linked to the signal.'}
          </span>
          {fromSignal.data?.existingSetupId && (
            <Link to={`/setups/${fromSignal.data.existingSetupId}`} className="self-start text-sm font-semibold">
              You already made a setup from this signal →
            </Link>
          )}
        </Card>
      )}
      <div className="flex flex-wrap items-start gap-4">
        <section className="flex min-w-0 flex-[999_1_480px] flex-col gap-3.5">
          <Card className="grid grid-cols-2 gap-3 p-4 md:grid-cols-4">
            <Field label="Symbol" className="col-span-2 md:col-span-1">
              <Select value={inst ?? ''} onChange={(e) => setInstrumentId(e.target.value)} disabled={editing}>
                {editing ? (
                  <option value={existing!.instrumentId}>{existing!.symbol}</option>
                ) : (
                  symbols.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.symbol} · {s.marketCode}
                    </option>
                  ))
                )}
              </Select>
            </Field>
            <Field label="Timeframe">
              <Select value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
                {[...new Set([...TIMEFRAMES, timeframe])].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </Select>
            </Field>
            <Field label="Strategy (optional)" hint={opts && !opts.strategies.length ? 'No strategies in the library yet' : undefined}>
              <Select value={strategyId} onChange={(e) => setStrategyId(e.target.value)} disabled={!opts?.strategies.length}>
                <option value="">None</option>
                {opts?.strategies.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.code} · {s.name}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="col-span-2 flex flex-col gap-1.5 md:col-span-1">
              <span className="text-xs text-muted">Direction</span>
              {editing ? (
                <span className="py-2.5 font-semibold">{existing!.direction === 'LONG' ? '▲ Long' : '▼ Short'}</span>
              ) : (
                <Segmented label="Direction" options={[{ value: 'LONG', label: '▲ Long' }, { value: 'SHORT', label: '▼ Short' }]} value={direction} onChange={setDirection} colors={{ LONG: '#3dd68c', SHORT: '#ff7a6e' }} />
              )}
            </div>
            <Field label="Zone from">
              <Input mono inputMode="decimal" value={zoneLow} onChange={(e) => setZoneLow(e.target.value)} />
            </Field>
            <Field label="Zone to (optional)">
              <Input mono inputMode="decimal" value={zoneHigh} onChange={(e) => setZoneHigh(e.target.value)} />
            </Field>
            <Field label="Invalidation level" className="col-span-2" hint={levelsIssue ? <span className="text-warn">{levelsIssue}</span> : 'Where the idea is wrong. It becomes the stop when you convert to a plan.'}>
              <Input mono inputMode="decimal" value={invalidation} onChange={(e) => setInvalidation(e.target.value)} className="text-down" />
            </Field>
            <Field label="Trigger condition" className="col-span-2 md:col-span-4">
              <Textarea value={trigger} onChange={(e) => setTrigger(e.target.value)} placeholder="What has to happen for you to act, e.g. bullish engulfing on H1 inside the zone" />
            </Field>
            <Field label="Target idea (optional)" className="col-span-2" hint="Prices here pre-fill the plan's targets when you convert.">
              <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="e.g. prior high 221.40, then 224" />
            </Field>
            <div className="col-span-2 flex flex-col gap-1.5">
              <span className="text-xs text-muted">Confidence</span>
              <Segmented label="Confidence" options={CONFIDENCE} value={confidence} onChange={setConfidence} />
            </div>
          </Card>
        </section>

        <aside aria-label="Updates and expiry" className="flex min-w-0 flex-[1_1_340px] flex-col gap-3.5">
          <Card className="flex flex-col gap-3 p-4">
            <h2 className="m-0 text-[15px] font-semibold">How often you'll update it</h2>
            <Field label="Update frequency" hint={cadenceHint}>
              <Select value={freq} onChange={(e) => setFreq(e.target.value)}>
                {SETUP_FREQUENCIES.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </Select>
            </Field>
            {freq === 'custom' && (
              <Field label="Every (minutes)">
                <Input mono inputMode="numeric" value={customMinutes} onChange={(e) => setCustomMinutes(e.target.value.replace(/\D/g, ''))} />
              </Field>
            )}
            <span className="text-xs text-muted">Miss an update and you get a reminder; miss the reminder and the setup shows as Stale to the floor and your Master Trader.</span>
          </Card>
          <Card className="flex flex-col gap-3 p-4">
            <h2 className="m-0 text-[15px] font-semibold">Expiry</h2>
            {!editing && (
              <label className="flex cursor-pointer items-start gap-2.5">
                <input type="radio" name="expiry" className="mt-1 h-4 w-4" checked={!ownExpiry} onChange={() => setOwnExpiry(false)} />
                <span>
                  Default{market ? `: ${fmtTime(market.defaultExpiry, tz, true)}` : ''}
                  <br />
                  <span className="text-xs text-muted">{market ? ruleText(market.expiryRule, market.code) : ''}</span>
                </span>
              </label>
            )}
            {editing && <span className="text-sm text-ink2">Currently {fmtTime(existing!.expiresAt, tz, true)}</span>}
            <label className="flex cursor-pointer items-center gap-2.5">
              <input type="radio" name="expiry" className="h-4 w-4" checked={ownExpiry} onChange={() => setOwnExpiry(true)} />
              <span>{editing ? 'Change the expiry' : 'Pick my own time'}</span>
            </label>
            {ownExpiry && (
              <Field label={`Expires at (${tz})`}>
                <Input type="datetime-local" value={expiryLocal} onChange={(e) => setExpiryLocal(e.target.value)} />
              </Field>
            )}
          </Card>
          <Card className="p-4">
            <Field label="Chart link (optional)" hint="TradingView chart or screenshot link">
              <Input type="url" inputMode="url" value={chartUrl} onChange={(e) => setChartUrl(e.target.value)} placeholder="https://www.tradingview.com/x/…" />
            </Field>
          </Card>
        </aside>
      </div>
    </>
  );
}
